import type {
    ContentScrubEditorState,
    ContentScrubStored,
    GlobalConfigurationContentScrubResponse,
} from '../types/global/contentScrub.types';

/** Code defaults — fallback when GET omits read-only fields */
export const CODE_DEFAULT_ALLOWED_LINK_DOMAINS = [
    'youtube.com',
    'youtu.be',
    'dropbox.com',
    'drive.google.com',
    'docs.google.com',
    'thspian.com',
    'thspian.me',
    'meeteor.me',
    'meeteor.co.uk',
] as const;

export const CODE_DEFAULT_SCRUB_PLATFORM_NAMES = ['instagram', 'tiktok', 'facebook', 'linkedin'] as const;

const CONTENT_SCRUB_PATCH_KEYS: (keyof ContentScrubStored)[] = [
    'allowed_link_domains',
    'allowed_platform_names',
    'scrub_platform_names',
];

/** Derive admin-editable extras from enriched GET response */
export function contentScrubExtras(merged: GlobalConfigurationContentScrubResponse): ContentScrubStored {
    const defaultLinks = new Set(merged.default_allowed_link_domains.map(d => d.toLowerCase()));
    const defaultScrub = new Set(merged.default_scrub_platform_names.map(p => p.toLowerCase()));

    return {
        allowed_link_domains: merged.allowed_link_domains.filter(d => !defaultLinks.has(d.toLowerCase())),
        allowed_platform_names: [...merged.allowed_platform_names],
        scrub_platform_names: merged.scrub_platform_names.filter(p => !defaultScrub.has(p.toLowerCase())),
    };
}

export function enrichContentScrubForEditor(
    merged?: Partial<GlobalConfigurationContentScrubResponse> | null,
): ContentScrubEditorState {
    const defaultLinks = merged?.default_allowed_link_domains?.length
        ? merged.default_allowed_link_domains
        : [...CODE_DEFAULT_ALLOWED_LINK_DOMAINS];
    const defaultScrub = merged?.default_scrub_platform_names?.length
        ? merged.default_scrub_platform_names
        : [...CODE_DEFAULT_SCRUB_PLATFORM_NAMES];

    const full: GlobalConfigurationContentScrubResponse = {
        allowed_link_domains: merged?.allowed_link_domains ?? [...defaultLinks],
        allowed_platform_names: merged?.allowed_platform_names ?? [],
        scrub_platform_names: merged?.scrub_platform_names ?? [...defaultScrub],
        default_allowed_link_domains: defaultLinks,
        default_scrub_platform_names: defaultScrub,
    };

    return {
        ...contentScrubExtras(full),
        default_allowed_link_domains: defaultLinks,
        default_scrub_platform_names: defaultScrub,
    };
}

export function dedupeStrings(items: string[]): string[] {
    const seen = new Set<string>();
    return items.filter(item => {
        const key = item.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** Hostname only — e.g. vimeo.com, not https://vimeo.com/... */
export function normalizeLinkDomain(input: string): string | null {
    let s = input.trim().toLowerCase();
    if (!s) return null;

    s = s.replace(/^https?:\/\//, '');
    s = s.replace(/^www\./, '');
    s = s.split('/')[0]?.split(':')[0]?.split('?')[0] ?? '';
    if (!s) return null;

    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(s)) {
        return null;
    }

    return s;
}

/** Single token — letters and numbers only */
export function normalizePlatformName(input: string): string | null {
    const s = input.trim().toLowerCase();
    if (!s || !/^[a-z0-9]+$/.test(s)) return null;
    return s;
}

/** Strip read-only default_* fields; keep only PATCH keys present in the diff */
export function sanitizeContentScrubPatch<T extends { content_scrub?: unknown }>(patch: T): T {
    if (!patch.content_scrub || typeof patch.content_scrub !== 'object') return patch;

    const scrub = patch.content_scrub as Record<string, unknown>;
    const clean: Partial<ContentScrubStored> = {};

    CONTENT_SCRUB_PATCH_KEYS.forEach(key => {
        if (Array.isArray(scrub[key])) {
            clean[key] = dedupeStrings(scrub[key] as string[]);
        }
    });

    if (Object.keys(clean).length === 0) {
        const { content_scrub: _removed, ...rest } = patch;
        return rest as T;
    }

    return { ...patch, content_scrub: clean as ContentScrubStored };
}
